/**
 * The in-flight / failed rollup above the source list — §2.8 #99.
 *
 * Extracted from `SourcesPanel`, where it was an IIFE inside the list.
 *
 * It answers one question — "is anything still happening?" — without making
 * the user read every row. Which is why it reports the rollup and not the
 * detail: the per-source status pills are right there underneath.
 *
 * **Processing wins over failed** when both are present, deliberately. A
 * partially-failed batch that is still working is not a failure yet, and
 * leading with red would tell the user to act while the outcome is still
 * changing. Once nothing is in flight, the failure count takes over.
 *
 * Shows nothing on the happy path. Every source ready is the common case,
 * and a green "all good" banner is noise in a panel the user is scanning —
 * but a batch finishing IS said aloud, into a live region that stays mounted
 * (see the render). Tested in `SourceIngestBanner.test.tsx`: a transition is
 * not an args state, so the component stories cannot hold it.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SourceIngestBannerProps {
  /** Sources visible in the current view. */
  total: number;
  /** Of those, how many are pending or processing. */
  inFlight: number;
  /** Of those, how many failed. */
  failed: number;
}

/**
 * Whether a batch has just settled — `inFlight` went from something to zero.
 *
 * Derived during render from the previous prop rather than in an effect, so
 * the "ready" line lands in the same commit as the processing line leaves and
 * the live region never reads empty in between.
 */
function useJustFinished(inFlight: number): boolean {
  const [prev, setPrev] = useState(inFlight);
  const [finished, setFinished] = useState(false);
  if (inFlight !== prev) {
    setPrev(inFlight);
    setFinished(prev > 0 && inFlight === 0);
  }
  return finished;
}

export function SourceIngestBanner({
  total,
  inFlight,
  failed,
}: SourceIngestBannerProps) {
  const justFinished = useJustFinished(inFlight);
  const visible = inFlight > 0 || failed > 0;

  return (
    // The live region is PERMANENT, and its content is what changes. It used
    // to be created together with its first message and removed when the last
    // source settled - and screen readers announce neither a region that
    // arrives already filled nor one that disappears, so "is anything still
    // happening?" was never answered aloud. `role='status'` is polite: an
    // ingest finishing is worth saying at the next pause, not worth
    // interrupting for.
    <div role='status'>
      {visible ? (
        <div
          className={cn(
            'mb-1 flex items-center gap-2 rounded-md border px-3 py-2 text-xs',
            inFlight > 0
              ? // Processing - the brand accent, matching the panel and the pill.
                // Dark was `bg-primary/30 text-primary`: lavender text on a
                // lavender tint, which failed axe's contrast check the first
                // time the banner was rendered dark. The text steps up the
                // ramp instead and the tint halves.
                'border-brand-200/60 bg-brand-50/70 text-brand-800 dark:border-primary/30 dark:bg-primary/15 dark:text-brand-200'
              : // All settled, some failed - matching the failed pill and icon.
                'border-destructive/30 bg-destructive/10 text-destructive-emphasis'
          )}
        >
          {inFlight > 0 ? (
            <>
              <Loader2
                className='h-3.5 w-3.5 flex-shrink-0 animate-spin'
                aria-hidden='true'
              />
              <span>
                Processing {inFlight} of {total} source{total === 1 ? '' : 's'}…
              </span>
            </>
          ) : (
            <>
              <span
                className='inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-destructive'
                aria-hidden='true'
              />
              <span>
                {failed} source{failed === 1 ? '' : 's'} failed to process
              </span>
            </>
          )}
        </div>
      ) : justFinished ? (
        // Said, not shown: the pills underneath already turned "Ready", and a
        // green "all good" box is the noise the header comment rules out.
        <span className='sr-only'>
          {total === 1
            ? 'Your source is ready.'
            : `All ${total} sources are ready.`}
        </span>
      ) : null}
    </div>
  );
}
